// src/pages/agrilink/components/AgriMetaverseMarketplace.jsx
import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { motion } from "framer-motion";

const marketplaceData = {
  Vegetables: {
    "Local Market": ["Potato", "Onion", "All Vegetables"],
    "Green Bazaar": ["Tomato", "Spinach", "All Vegetables"],
  },
  Fruits: {
    "Fruit Market": ["Apple", "Orange", "All Fruits"],
    "Green Bazaar": ["Banana", "Mango", "All Fruits"],
  },
  Spices: {
    "Spice Hub": ["Turmeric", "Cumin Seeds", "All Spices"],
  },
  Dairy: {
    "Dairy Co-op": ["Milk", "Cheese", "All Dairy"],
  },
  Poultry: {
    "Poultry Hub": ["Eggs", "Chicken", "All Poultry"],
  },
  Fish: {
    "Fisheries Hub": ["Tilapia", "Catfish", "All Fish"],
  },
};

const sellersData = {
  Potato: ["Farmer A", "Farmer B", "Farmer C"],
  Onion: ["Farmer D", "Farmer E"],
  Tomato: ["Farmer F", "Farmer G"],
  Apple: ["Farmer H", "Farmer I"],
  Orange: ["Farmer J", "Farmer K"],
  Turmeric: ["Spice Seller 1", "Spice Seller 2"],
  "Cumin Seeds": ["Spice Seller 3"],
  Milk: ["Dairy Farmer 1", "Dairy Farmer 2"],
  Eggs: ["Poultry Seller 1", "Poultry Seller 2"],
  Tilapia: ["Fisheries Seller 1", "Fisheries Seller 2"],
};

const productImages = {
  Potato: "https://images.unsplash.com/photo-1582515073490-399813f1d4e8?auto=format&fit=crop&w=800&q=60",
  Onion: "https://images.unsplash.com/photo-1587305338530-30c7f8f3cdb3?auto=format&fit=crop&w=800&q=60",
  Tomato: "https://images.unsplash.com/photo-1582281298054-2f86f4e93b8a?auto=format&fit=crop&w=800&q=60",
  Apple: "https://images.unsplash.com/photo-1571046812001-b28b7f6d42e2?auto=format&fit=crop&w=800&q=60",
  Orange: "https://images.unsplash.com/photo-1560807707-8cc77767d783?auto=format&fit=crop&w=800&q=60",
  Turmeric: "https://images.unsplash.com/photo-1601004890684-d8cbf643f5f2?auto=format&fit=crop&w=800&q=60",
  "Cumin Seeds": "https://images.unsplash.com/photo-1627569798091-7cf9cf61c0da?auto=format&fit=crop&w=800&q=60",
  Milk: "https://images.unsplash.com/photo-1589923188900-1d5f8e0f6118?auto=format&fit=crop&w=800&q=60",
  Eggs: "https://images.unsplash.com/photo-1587049352856-4f61f9634828?auto=format&fit=crop&w=800&q=60",
  Tilapia: "https://images.unsplash.com/photo-1613463326783-02f9f5488cb7?auto=format&fit=crop&w=800&q=60",
};

const AgriMetaverseMarketplace = () => {
  const [category, setCategory] = useState("");
  const [market, setMarket] = useState("");
  const [product, setProduct] = useState("");

  const availableMarkets = category ? Object.keys(marketplaceData[category]) : [];
  const availableProducts = category && market ? marketplaceData[category][market] : [];
  const sellers = product ? sellersData[product] || [] : [];

  // Function to randomly show "This Seller has an Offer"
  const hasOffer = () => Math.random() < 0.3; // 30% chance

  return (
    <div className="w-full max-w-6xl mx-auto mt-12 p-6 bg-gray-500">
      <h2 className="text-3xl font-bold text-yellow-200 mb-6 text-center">🛒 AgriMetaverse Marketplace</h2>

      {/* Selection Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-8 justify-center items-center">
        <select
          className="border p-2 rounded-lg h-10"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setMarket("");
            setProduct("");
          }}
        >
          <option value="">Select Category</option>
          {Object.keys(marketplaceData).map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <select
          className="border p-2 rounded-lg h-10"
          value={market}
          onChange={(e) => {
            setMarket(e.target.value);
            setProduct("");
          }}
          disabled={!category}
        >
          <option value="">Select Market</option>
          {availableMarkets.map((mkt) => (
            <option key={mkt} value={mkt}>{mkt}</option>
          ))}
        </select>

        <select
          className="border p-2 rounded-lg h-10"
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          disabled={!market}
        >
          <option value="">Select Product</option>
          {availableProducts.map((prod) => (
            <option key={prod} value={prod}>{prod}</option>
          ))}
        </select>
      </div>

      {/* Product Backdrop with Sellers Overlay */}
      {product && productImages[product] && (
        <div className="relative w-full h-[500px] rounded-2xl shadow-lg overflow-hidden">
          {/* Background Image */}
          <img
            src={productImages[product]}
            alt={product}
            className="w-full h-full object-cover"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-30 flex flex-col">
            {/* Market Name */}
            {market && (
              <h3 className="text-center text-2xl md:text-3xl font-bold text-white mt-4">{market}</h3>
            )}

            {/* Sellers Grid */}
            <div className="flex-1 p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto">
              {sellers.map((seller) => (
                <motion.div
                  key={seller}
                  className="bg-white bg-opacity-80 rounded-lg p-4 shadow-md flex flex-col justify-between hover:scale-105 transition-transform relative"
                >
                  <div className="font-semibold text-green-900">{seller}</div>

                  {/* Random Flash Offer */}
                  {hasOffer() && (
                    <span className="absolute top-2 right-2 bg-yellow-400 text-xs px-2 py-1 rounded-full animate-pulse">
                      This Seller has an Offer
                    </span>
                  )}

                  {/* <button
                    className="mt-4 bg-green-700 text-white px-3 py-1 rounded-lg hover:bg-green-800"
                    onClick={() =>
                      navigate("/agrilink-digitaltwin", { state: { seller, product, market } })
                    }
                  >
                    Digital Twin
                  </button> */}
                  <Link
                    to="/twiworld"
                    state={{ seller, product, market }}
                    className="mt-4 bg-green-700 text-white px-3 py-1 rounded-lg hover:bg-green-800 text-center"
                  >
                    Digital Twin
                  </Link>
                </motion.div>
              ))}
              {sellers.length === 0 && (
                <p className="text-white text-center col-span-full">No sellers found for {product}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgriMetaverseMarketplace;
